/**
 * Billing store module (Phase 5a — `/my/billing`).
 *
 * Holds the org-scoped subscription summary, invoices list (with a per-id
 * detail cache), payment-methods list, and notification preferences for the
 * customer billing page. The page itself is auth-gated; this module only
 * dispatches the relevant load action when the user navigates in.
 *
 * Namespaced, NOT persisted (live billing data is always re-fetched).
 *
 * The store deliberately distinguishes "the provider isn't configured yet"
 * (a typed `BillingProviderNotConfigured` error from the service layer) from
 * a generic load failure — the UI surfaces the former as an informational
 * "payments coming soon" card and the latter as a retry banner.
 */
import {
  billingApi,
  BillingProviderNotConfigured
} from '@/services/billing-api'

const state = () => ({
  // ── Subscription ───────────────────────────────────────────────────────
  subscription: null,
  subLoading: false,
  subError: null,

  // ── Invoices ───────────────────────────────────────────────────────────
  invoices: [], // list (current page)
  invoicesCount: 0,
  invoicesPagination: null,
  invoicesLoading: false,
  invoicesError: null,
  invoiceFilter: null, // status filter ('paid'|'open'|...)
  invoicePage: 1,
  invoicePageSize: 20,
  invoiceDetail: {}, // id -> full invoice (with line_items)

  // Per-action busy maps keyed by invoice id (download / email)
  invoiceBusy: {},

  // ── Payment methods ────────────────────────────────────────────────────
  paymentMethods: [],
  pmLoading: false,
  pmError: null,
  // When set, the "add payment method" attempt came back 503 (P5a). The
  // billing page renders the friendly informational card instead of the
  // (empty) provider-element placeholder.
  providerComingSoon: null, // { provider, nextStep, message } | null

  // ── Notification preferences ───────────────────────────────────────────
  prefs: null,
  prefsLoading: false,
  prefsError: null,
  prefsSaving: false
})

const getters = {
  hasSubscription: (s) => Boolean(s.subscription),
  currentPlanName: (s) =>
    s.subscription?.plan_snapshot?.name
      || s.subscription?.plan?.name
      || s.subscription?.plan_name
      || null,
  currentTier: (s) =>
    s.subscription?.plan_snapshot?.tier
      || s.subscription?.tier
      || null,
  isProviderComingSoon: (s) => Boolean(s.providerComingSoon)
}

const mutations = {
  // Subscription
  SET_SUB_LOADING(state, v) { state.subLoading = v },
  SET_SUB_ERROR(state, v) { state.subError = v },
  SET_SUB(state, v) { state.subscription = v || null },

  // Invoices
  SET_INVOICES_LOADING(state, v) { state.invoicesLoading = v },
  SET_INVOICES_ERROR(state, v) { state.invoicesError = v },
  SET_INVOICES(state, { items, count, pagination }) {
    state.invoices = Array.isArray(items) ? items : []
    state.invoicesCount = count ?? state.invoices.length
    state.invoicesPagination = pagination || null
  },
  SET_INVOICE_FILTER(state, v) { state.invoiceFilter = v || null },
  SET_INVOICE_PAGE(state, v) { state.invoicePage = v || 1 },
  SET_INVOICE_PAGE_SIZE(state, v) { state.invoicePageSize = v || 20 },
  SET_INVOICE_DETAIL(state, { id, invoice }) {
    state.invoiceDetail = { ...state.invoiceDetail, [id]: invoice }
  },
  SET_INVOICE_BUSY(state, { id, action, busy }) {
    const key = `${id}:${action}`
    const next = { ...state.invoiceBusy }
    if (busy) next[key] = true
    else delete next[key]
    state.invoiceBusy = next
  },

  // Payment methods
  SET_PM_LOADING(state, v) { state.pmLoading = v },
  SET_PM_ERROR(state, v) { state.pmError = v },
  SET_PAYMENT_METHODS(state, v) {
    state.paymentMethods = Array.isArray(v) ? v : []
  },
  SET_PROVIDER_COMING_SOON(state, v) {
    state.providerComingSoon = v || null
  },

  // Prefs
  SET_PREFS_LOADING(state, v) { state.prefsLoading = v },
  SET_PREFS_ERROR(state, v) { state.prefsError = v },
  SET_PREFS_SAVING(state, v) { state.prefsSaving = v },
  SET_PREFS(state, v) { state.prefs = v || null }
}

const actions = {
  // ── Subscription ───────────────────────────────────────────────────────
  async loadSubscription({ commit }) {
    commit('SET_SUB_LOADING', true)
    commit('SET_SUB_ERROR', null)
    try {
      const sub = await billingApi.getSubscription()
      commit('SET_SUB', sub)
      return sub
    } catch (error) {
      commit('SET_SUB_ERROR', error?.message || 'Failed to load subscription')
      throw error
    } finally {
      commit('SET_SUB_LOADING', false)
    }
  },

  async cancelSubscription({ commit, dispatch }, { atPeriodEnd = true } = {}) {
    const result = await billingApi.cancelSubscription({ atPeriodEnd })
    // Re-pull so the UI reflects cancel_at_period_end / status changes.
    await dispatch('loadSubscription')
    return result
  },

  // ── Invoices ───────────────────────────────────────────────────────────
  async loadInvoices({ commit, state }, {
    status,
    page,
    pageSize,
    silent = false
  } = {}) {
    const filter = status !== undefined ? status : state.invoiceFilter
    const p = page || state.invoicePage
    const ps = pageSize || state.invoicePageSize
    commit('SET_INVOICE_FILTER', filter)
    commit('SET_INVOICE_PAGE', p)
    commit('SET_INVOICE_PAGE_SIZE', ps)
    if (!silent) {
      commit('SET_INVOICES_LOADING', true)
      commit('SET_INVOICES_ERROR', null)
    }
    try {
      const result = await billingApi.listInvoices({
        status: filter,
        page: p,
        pageSize: ps
      })
      commit('SET_INVOICES', result)
      return result
    } catch (error) {
      if (!silent) {
        commit('SET_INVOICES_ERROR', error?.message || 'Failed to load invoices')
      }
      throw error
    } finally {
      if (!silent) commit('SET_INVOICES_LOADING', false)
    }
  },

  async loadInvoice({ commit, state }, id) {
    if (state.invoiceDetail[id]) return state.invoiceDetail[id]
    const invoice = await billingApi.getInvoice(id)
    commit('SET_INVOICE_DETAIL', { id, invoice })
    return invoice
  },

  async downloadInvoice({ commit, dispatch }, { id, number }) {
    commit('SET_INVOICE_BUSY', { id, action: 'download', busy: true })
    try {
      const suggested = number ? `invoice-${number}.pdf` : `invoice-${id}.pdf`
      const filename = await billingApi.downloadInvoice(id, suggested)
      dispatch('showNotification', {
        type: 'success',
        message: `Saved ${filename}.`
      }, { root: true })
      return filename
    } catch (error) {
      dispatch('showNotification', {
        type: 'error',
        message: error?.message || 'Couldn’t download that invoice.'
      }, { root: true })
      throw error
    } finally {
      commit('SET_INVOICE_BUSY', { id, action: 'download', busy: false })
    }
  },

  async emailInvoice({ commit, dispatch }, { id, number }) {
    commit('SET_INVOICE_BUSY', { id, action: 'email', busy: true })
    try {
      await billingApi.emailInvoice(id)
      dispatch('showNotification', {
        type: 'success',
        message: `Invoice ${number || id} sent to your registered email.`
      }, { root: true })
    } catch (error) {
      dispatch('showNotification', {
        type: 'error',
        message: error?.message || 'Couldn’t email that invoice.'
      }, { root: true })
      throw error
    } finally {
      commit('SET_INVOICE_BUSY', { id, action: 'email', busy: false })
    }
  },

  // ── Payment methods ────────────────────────────────────────────────────
  async loadPaymentMethods({ commit }) {
    commit('SET_PM_LOADING', true)
    commit('SET_PM_ERROR', null)
    try {
      const list = await billingApi.listPaymentMethods()
      commit('SET_PAYMENT_METHODS', list)
      return list
    } catch (error) {
      commit('SET_PM_ERROR', error?.message || 'Failed to load payment methods')
      throw error
    } finally {
      commit('SET_PM_LOADING', false)
    }
  },

  /**
   * P5a: this always returns the typed BillingProviderNotConfigured. We
   * commit it to state so the page can render the friendly card.
   */
  async addPaymentMethod({ commit }, payload = {}) {
    try {
      const pm = await billingApi.addPaymentMethod(payload)
      // P5b: real PM was added; refresh the list.
      commit('SET_PROVIDER_COMING_SOON', null)
      return pm
    } catch (error) {
      if (error instanceof BillingProviderNotConfigured) {
        commit('SET_PROVIDER_COMING_SOON', {
          provider: error.provider,
          nextStep: error.nextStep,
          message: error.message
        })
        return null
      }
      throw error
    }
  },

  async removePaymentMethod({ dispatch }, id) {
    await billingApi.removePaymentMethod(id)
    await dispatch('loadPaymentMethods')
  },

  async setDefaultPaymentMethod({ dispatch }, id) {
    await billingApi.setDefaultPaymentMethod(id)
    await dispatch('loadPaymentMethods')
  },

  // ── Notification preferences ───────────────────────────────────────────
  async loadPrefs({ commit }) {
    commit('SET_PREFS_LOADING', true)
    commit('SET_PREFS_ERROR', null)
    try {
      const prefs = await billingApi.getNotificationPrefs()
      commit('SET_PREFS', prefs)
      return prefs
    } catch (error) {
      commit('SET_PREFS_ERROR', error?.message || 'Failed to load preferences')
      throw error
    } finally {
      commit('SET_PREFS_LOADING', false)
    }
  },

  async updatePrefs({ commit, dispatch }, payload) {
    commit('SET_PREFS_SAVING', true)
    try {
      const prefs = await billingApi.updateNotificationPrefs(payload)
      commit('SET_PREFS', prefs)
      dispatch('showNotification', {
        type: 'success',
        message: 'Notification preferences updated.'
      }, { root: true })
      return prefs
    } catch (error) {
      dispatch('showNotification', {
        type: 'error',
        message: error?.message || 'Couldn’t save preferences.'
      }, { root: true })
      throw error
    } finally {
      commit('SET_PREFS_SAVING', false)
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
